import { Injectable } from '@angular/core';

interface StoredToken {
	token: string;
	expiry: number;
}

@Injectable({ providedIn: 'root' })
export class AuthTokenStoreService {
	private static readonly INITIAL_TOKEN_KEY = 'auth_initial_token';
	private static readonly EVALUATION_TOKEN_KEY = 'auth_evaluation_token';

	private initialToken: string | null = null;
	private initialTokenExpiry: number | null = null;
	private evaluationToken: string | null = null;
	private evaluationTokenExpiry: number | null = null;

	constructor() {
		this.hydrateFromStorage();
	}

	setInitialToken(token: string, expiresIn: number): void {
		this.initialToken = token;
		this.initialTokenExpiry = Date.now() + expiresIn * 1000;
		this.writeStored(
			AuthTokenStoreService.INITIAL_TOKEN_KEY,
			this.initialToken,
			this.initialTokenExpiry
		);
	}

	setEvaluationToken(token: string, expiresIn: number): void {
		this.evaluationToken = token;
		this.evaluationTokenExpiry = Date.now() + expiresIn * 1000;
		this.writeStored(
			AuthTokenStoreService.EVALUATION_TOKEN_KEY,
			this.evaluationToken,
			this.evaluationTokenExpiry
		);
	}

	getInitialToken(): string | null {
		return this.initialToken;
	}

	getEvaluationToken(): string | null {
		return this.evaluationToken;
	}

	clearAll(): void {
		this.initialToken = null;
		this.initialTokenExpiry = null;
		this.evaluationToken = null;
		this.evaluationTokenExpiry = null;
		this.removeStored(AuthTokenStoreService.INITIAL_TOKEN_KEY);
		this.removeStored(AuthTokenStoreService.EVALUATION_TOKEN_KEY);
	}

	isInitialTokenValid(): boolean {
		return (
			this.initialToken !== null &&
			this.initialTokenExpiry !== null &&
			Date.now() < this.initialTokenExpiry
		);
	}

	isEvaluationTokenValid(): boolean {
		return (
			this.evaluationToken !== null &&
			this.evaluationTokenExpiry !== null &&
			Date.now() < this.evaluationTokenExpiry
		);
	}

	private hydrateFromStorage(): void {
		const initial = this.readStored(AuthTokenStoreService.INITIAL_TOKEN_KEY);
		if (initial) {
			this.initialToken = initial.token;
			this.initialTokenExpiry = initial.expiry;
		}

		const evaluation = this.readStored(
			AuthTokenStoreService.EVALUATION_TOKEN_KEY
		);
		if (evaluation) {
			this.evaluationToken = evaluation.token;
			this.evaluationTokenExpiry = evaluation.expiry;
		}
	}

	private readStored(key: string): StoredToken | null {
		try {
			const raw = sessionStorage.getItem(key);
			if (!raw) {
				return null;
			}
			const parsed = JSON.parse(raw) as StoredToken;
			if (
				!parsed?.token ||
				typeof parsed.expiry !== 'number' ||
				Date.now() >= parsed.expiry
			) {
				sessionStorage.removeItem(key);
				return null;
			}
			return parsed;
		} catch (error) {
			console.log(
				'🚀 ~ file: auth-token-store.service.ts ~ readStored ~ error:',
				error
			);
			return null;
		}
	}

	private writeStored(key: string, token: string, expiry: number): void {
		try {
			sessionStorage.setItem(key, JSON.stringify({ token, expiry }));
		} catch (error) {
			console.log(
				'🚀 ~ file: auth-token-store.service.ts ~ writeStored ~ error:',
				error
			);
		}
	}

	private removeStored(key: string): void {
		try {
			sessionStorage.removeItem(key);
		} catch (error) {
			console.log(
				'🚀 ~ file: auth-token-store.service.ts ~ removeStored ~ error:',
				error
			);
		}
	}
}
